import React from 'react';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import moment from 'moment';
import { Paginator } from 'primereact/paginator';
import { Button } from 'primereact/button';
import CBMediaQuery from '../../shared/CBMediaQuery';
import CBDatePickerInput from '../../shared/CBDatePickerInput';
import CBSelectFilters from '../../shared/CBSelectFilters';
import CBMobileCard from '../../shared/CBMobileCard';
import { finnishDateFormat, getStyle } from '../../../utils/utils';
import PurchaseInvoiceDetail from './PurchaseInvoiceDetail';
import {
  fetchPurchaseInvoices as _fetchPurchaseInvoices,
  triggerPurchaseInvoiceSync as _triggerPurchaseInvoiceSync,
} from '../../../actions/chainActions';

function formatPrice(value) {
  if (value === null || value === undefined) return '-';
  return Number(value).toLocaleString('fi-FI', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + ' \u20AC';
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('fi-FI');
}

var statusColors = {
  new: { bg: 'rgba(255, 255, 255, 0.1)', color: 'rgba(255, 255, 255, 0.7)', border: 'rgba(255, 255, 255, 0.2)' },
  matched: { bg: 'rgba(100, 181, 246, 0.2)', color: '#90CAF9', border: 'rgba(100, 181, 246, 0.4)' },
  enriched: { bg: 'rgba(129, 199, 132, 0.2)', color: '#A5D6A7', border: 'rgba(129, 199, 132, 0.4)' },
  included: { bg: 'rgba(186, 104, 200, 0.2)', color: '#CE93D8', border: 'rgba(186, 104, 200, 0.4)' },
  excluded: { bg: 'rgba(255, 183, 77, 0.2)', color: '#FFB74D', border: 'rgba(255, 183, 77, 0.4)' },
  error: { bg: 'rgba(229, 115, 115, 0.2)', color: '#EF9A9A', border: 'rgba(229, 115, 115, 0.4)' },
};

function getStatusBadgeStyle(status) {
  var c = statusColors[status] || statusColors.new;
  return {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 600,
    backgroundColor: c.bg,
    color: c.color,
    border: '1px solid ' + c.border,
  };
}

var holdBadgeStyle = getStatusBadgeStyle('excluded');
var creditNoteBadgeStyle = getStatusBadgeStyle('error');

class PurchaseInvoiceList extends React.Component {
  state = {
    filters: {},
    page: 1,
    selectedInvoiceId: null,
  };

  componentWillMount() {
    this.refetch();
  }

  componentDidUpdate(prevProps) {
    if (this.props.chainId && this.props.chainId !== prevProps.chainId) {
      this.setState({ page: 1, filters: {} }, function () {
        this.refetch();
      }.bind(this));
    }
    var prevSync = prevProps.purchaseInvoiceSyncStatus;
    var currSync = this.props.purchaseInvoiceSyncStatus;
    if (prevSync && prevSync.loading && currSync && !currSync.loading && !currSync.error) {
      this.refetch();
    }
  }

  refetch = function () {
    var chainId = this.props.chainId;
    if (!chainId) return;
    var filters = this.state.filters;
    var page = this.state.page;
    var params = { ...filters, page: page, ordering: '-invoice_date' };
    this.props.fetchPurchaseInvoices(chainId, params);
  };

  changeFilter = function (name, value) {
    var filters = { ...this.state.filters };
    if (value) {
      filters[name] = value;
    } else {
      delete filters[name];
    }
    this.setState({ filters: filters, page: 1 }, function () {
      this.refetch();
    }.bind(this));
  };

  changePage = function (event) {
    var pagination = this.props.purchaseInvoicesPagination;
    var pageSize = pagination && pagination.page_size ? pagination.page_size : 20;
    var newPage = Math.floor(event.first / pageSize) + 1;
    this.setState({ page: newPage }, function () {
      this.refetch();
    }.bind(this));
  };

  handleSync = function () {
    var chainId = this.props.chainId;
    if (chainId) {
      this.props.triggerPurchaseInvoiceSync(chainId);
    }
  };

  openDetail = function (invoiceId) {
    this.setState({ selectedInvoiceId: invoiceId });
  };

  closeDetail = function () {
    this.setState({ selectedInvoiceId: null });
    this.refetch();
  };

  getWorkshopOptions = function () {
    var chainMemberships = this.props.chainMemberships;
    var t = this.props.t;
    var options = [{ value: '', label: t('allWorkshops') }];
    if (chainMemberships && chainMemberships.length > 0) {
      chainMemberships.forEach(function (m) {
        if (m.workshop && m.workshop.id) {
          options.push({ value: String(m.id), label: m.workshop.name });
        }
      });
    }
    return options;
  };

  getStatusOptions = function () {
    var t = this.props.t;
    return [
      { value: '', label: t('allStatuses') },
      { value: 'new', label: t('piStatusNew') },
      { value: 'matched', label: t('piStatusMatched') },
      { value: 'enriched', label: t('piStatusEnriched') },
      { value: 'included', label: t('piStatusIncluded') },
      { value: 'excluded', label: t('piStatusExcluded') },
      { value: 'error', label: t('piStatusError') },
    ];
  };

  getStatusLabel = function (status) {
    var t = this.props.t;
    var labels = {
      new: t('piStatusNew'),
      matched: t('piStatusMatched'),
      enriched: t('piStatusEnriched'),
      included: t('piStatusIncluded'),
      excluded: t('piStatusExcluded'),
      error: t('piStatusError'),
    };
    return labels[status] || status;
  };

  renderCard = (inv) => {
    var t = this.props.t;
    return (
      <CBMobileCard
        key={inv.id}
        title={inv.invoice_number || '-'}
        dialogTitle={inv.invoice_number || '-'}
        subtitle={inv.workshop_name || inv.supplier_name || ''}
        badges={[
          <span key='status' style={getStatusBadgeStyle(inv.status)}>{this.getStatusLabel(inv.status)}</span>,
        ].concat(inv.invoicing_hold ? [
          <span key='hold' style={holdBadgeStyle}>{t('invoicingHold')}</span>,
        ] : []).concat(Number(inv.total_amount) < 0 ? [
          <span key='credit' style={creditNoteBadgeStyle}>{t('creditNote')}</span>,
        ] : [])}
        fields={[
          { label: t('customer'), value: inv.customer_name || '-' },
          { label: t('invoiceDate'), value: formatDate(inv.invoice_date) },
          { label: t('amount'), value: formatPrice(inv.total_amount) },
        ]}
        allFields={[
          { label: t('invoiceNumber'), value: inv.invoice_number || '-' },
          { label: t('workshop'), value: inv.workshop_name || inv.supplier_name || '-' },
          { label: t('customer'), value: inv.customer_name || '-' },
          { label: t('invoiceDate'), value: formatDate(inv.invoice_date) },
          { label: t('amount'), value: formatPrice(inv.total_amount) },
          { label: t('status'), value: this.getStatusLabel(inv.status) },
        ]}
        onClick={() => this.openDetail(inv.id)}
      />
    );
  };

  renderMobileList = (screenSize) => {
    var t = this.props.t;
    var invoices = this.props.purchaseInvoices;
    var pagination = this.props.purchaseInvoicesPagination;
    return (
      <div style={getStyle(screenSize, styles, 'mobileListContainer')}>
        {invoices && invoices.length > 0 ? (
          invoices.map((inv) => this.renderCard(inv))
        ) : (
          <div style={styles.default.noDataCell}>{t('noPurchaseInvoices')}</div>
        )}
        {pagination && pagination.count > 0 && (
          <div style={styles.default.paginatorContainer}>
            <Paginator
              first={(pagination.page_size * (pagination.current_page - 1))}
              rows={pagination.page_size}
              totalRecords={pagination.count}
              onPageChange={this.changePage.bind(this)}
            />
          </div>
        )}
      </div>
    );
  };

  render() {
    var t = this.props.t;
    var invoices = this.props.purchaseInvoices;
    var status = this.props.purchaseInvoicesStatus;
    var pagination = this.props.purchaseInvoicesPagination;
    var syncStatus = this.props.purchaseInvoiceSyncStatus;
    var filters = this.state.filters;
    var selectedInvoiceId = this.state.selectedInvoiceId;
    var isLoading = status && status.loading;

    return (
      <CBMediaQuery>
        {function (screenSize) {
          return (
            <div style={getStyle(screenSize, styles, 'container')}>
              {/* Top actions */}
              <div style={getStyle(screenSize, styles, 'topActions')}>
                <Button
                  onClick={this.handleSync.bind(this)}
                  label={t('syncFromNetvisor')}
                  icon="pi pi-sync"
                  loading={syncStatus && syncStatus.loading}
                  style={styles.default.syncButton}
                  size="small"
                />
              </div>

              {/* Filters */}
              <div style={getStyle(screenSize, styles, 'filterRow')}>
                <CBSelectFilters
                  name="membership"
                  options={this.getWorkshopOptions()}
                  onChange={function (changed) {
                    this.changeFilter('membership', changed.membership);
                  }.bind(this)}
                  value={filters.membership || ''}
                  selectStyle={getStyle(screenSize, styles, 'selectFilter')}
                />
                <CBSelectFilters
                  name="status"
                  options={this.getStatusOptions()}
                  onChange={function (changed) {
                    this.changeFilter('status', changed.status);
                  }.bind(this)}
                  value={filters.status || ''}
                  selectStyle={getStyle(screenSize, styles, 'selectFilter')}
                />
                <CBSelectFilters
                  name="invoicing_hold"
                  options={[
                    { value: '', label: t('holdFilterAll') },
                    { value: 'true', label: t('holdFilterOnly') },
                  ]}
                  onChange={function (changed) {
                    this.changeFilter('invoicing_hold', changed.invoicing_hold);
                  }.bind(this)}
                  value={filters.invoicing_hold || ''}
                  selectStyle={getStyle(screenSize, styles, 'selectFilter')}
                />
                <div className="datepicker-dark" style={getStyle(screenSize, styles, 'datepickerWrapper')}>
                  <CBDatePickerInput
                    displayedDate={filters.date_from ? moment(filters.date_from) : null}
                    type="date"
                    dateFormat={finnishDateFormat}
                    placeholder={t('periodStart')}
                    changeDate={function (value) {
                      this.changeFilter('date_from', value ? moment(value).format('YYYY-MM-DD') : null);
                    }.bind(this)}
                  />
                </div>
                <div className="datepicker-dark" style={getStyle(screenSize, styles, 'datepickerWrapper')}>
                  <CBDatePickerInput
                    displayedDate={filters.date_to ? moment(filters.date_to) : null}
                    type="date"
                    dateFormat={finnishDateFormat}
                    placeholder={t('periodEnd')}
                    changeDate={function (value) {
                      this.changeFilter('date_to', value ? moment(value).format('YYYY-MM-DD') : null);
                    }.bind(this)}
                  />
                </div>
                <input
                  type="text"
                  placeholder={t('search')}
                  value={filters.search || ''}
                  onChange={function (e) {
                    this.changeFilter('search', e.target.value || null);
                  }.bind(this)}
                  style={getStyle(screenSize, styles, 'searchInput')}
                />
              </div>

              {/* Table (desktop) / Cards (mobile) */}
              {screenSize === 'small' ? (
                this.renderMobileList(screenSize)
              ) : (
              <div style={getStyle(screenSize, styles, 'tableContainer')}>
                {isLoading ? (
                  <div style={styles.default.loadingContainer}>
                    <p style={styles.default.loadingText}>Ladataan...</p>
                  </div>
                ) : (
                  <div>
                    <table style={getStyle(screenSize, styles, 'table')}>
                      <thead>
                        <tr>
                          <th style={styles.default.th}>{t('invoiceNumber')}</th>
                          <th style={styles.default.th}>{t('workshop')}</th>
                          <th style={styles.default.th}>{t('customer')}</th>
                          <th style={styles.default.th}>{t('invoiceDate')}</th>
                          <th style={{ ...styles.default.th, textAlign: 'right' }}>{t('amount')}</th>
                          <th style={styles.default.th}>{t('status')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices && invoices.length > 0 ? (
                          invoices.map(function (inv) {
                            return (
                              <tr
                                key={inv.id}
                                style={styles.default.tableRow}
                                onClick={function () { this.openDetail(inv.id); }.bind(this)}
                              >
                                <td style={styles.default.td}>{inv.invoice_number || '-'}</td>
                                <td style={styles.default.td}>{inv.workshop_name || inv.supplier_name || '-'}</td>
                                <td style={styles.default.td}>{inv.customer_name || '-'}</td>
                                <td style={styles.default.td}>{formatDate(inv.invoice_date)}</td>
                                <td style={{ ...styles.default.td, textAlign: 'right', fontWeight: 600 }}>
                                  {formatPrice(inv.total_amount)}
                                </td>
                                <td style={styles.default.td}>
                                  <span style={getStatusBadgeStyle(inv.status)}>
                                    {this.getStatusLabel(inv.status)}
                                  </span>
                                  {inv.invoicing_hold && (
                                    <span style={{ ...holdBadgeStyle, marginLeft: 6 }}>
                                      {t('invoicingHold')}
                                    </span>
                                  )}
                                  {Number(inv.total_amount) < 0 && (
                                    <span style={{ ...creditNoteBadgeStyle, marginLeft: 6 }}>
                                      {t('creditNote')}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          }.bind(this))
                        ) : (
                          <tr>
                            <td colSpan={6} style={styles.default.noDataCell}>
                              {t('noPurchaseInvoices')}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    {pagination && pagination.count > 0 && (
                      <div style={styles.default.paginatorContainer}>
                        <Paginator
                          first={(pagination.page_size * (pagination.current_page - 1))}
                          rows={pagination.page_size}
                          totalRecords={pagination.count}
                          onPageChange={this.changePage.bind(this)}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
              )}

              {selectedInvoiceId && (
                <PurchaseInvoiceDetail
                  invoiceId={selectedInvoiceId}
                  onClose={this.closeDetail.bind(this)}
                  onOpenConsolidated={this.props.onOpenConsolidated}
                />
              )}
            </div>
          );
        }.bind(this)}
      </CBMediaQuery>
    );
  }
}

var styles = {
  default: {
    container: {
      padding: '16px 16px',
    },
    mobileListContainer: {
      paddingBottom: 8,
    },
    topActions: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginBottom: 16,
    },
    syncButton: {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.25)',
      color: '#fff',
      fontWeight: 600,
    },
    filterRow: {
      display: 'flex',
      flexDirection: 'row',
      gap: 12,
      alignItems: 'center',
      flexWrap: 'wrap',
      marginBottom: 16,
    },
    selectFilter: {
      width: 180,
      marginLeft: 0,
      marginRight: 0,
    },
    datepickerWrapper: {
      width: 180,
      flexShrink: 0,
      cursor: 'pointer',
    },
    datepickerLabel: {
      color: 'rgba(255, 255, 255, 0.6)',
      fontSize: 13,
      marginRight: 6,
      whiteSpace: 'nowrap',
    },
    searchInput: {
      backgroundColor: 'rgba(0, 0, 0, 0.15)',
      border: '1px solid rgba(255, 255, 255, 0.25)',
      borderRadius: 4,
      color: '#fff',
      padding: '6px 12px',
      fontSize: 14,
      width: 200,
    },
    tableContainer: {
      overflowX: 'auto',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      whiteSpace: 'nowrap',
    },
    th: {
      padding: '10px 12px',
      color: 'rgba(255, 255, 255, 0.6)',
      fontSize: 12,
      fontWeight: 600,
      textTransform: 'uppercase',
      borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
      textAlign: 'left',
    },
    td: {
      padding: '12px 12px',
      color: '#fff',
      fontSize: 14,
      borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
    },
    tableRow: {
      cursor: 'pointer',
      transition: 'background-color 0.15s',
    },
    noDataCell: {
      padding: '32px 16px',
      color: 'rgba(255, 255, 255, 0.5)',
      fontSize: 14,
      textAlign: 'center',
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: 200,
    },
    loadingText: {
      color: 'rgba(255, 255, 255, 0.7)',
      fontSize: 14,
    },
    paginatorContainer: {
      display: 'flex',
      justifyContent: 'center',
      paddingTop: 16,
      paddingBottom: 24,
    },
  },
};

function mapStateToProps(state) {
  return {
    purchaseInvoices: state.chain.purchaseInvoices,
    purchaseInvoicesPagination: state.chain.purchaseInvoicesPagination,
    purchaseInvoicesStatus: state.chain.purchaseInvoicesStatus,
    purchaseInvoiceSyncStatus: state.chain.purchaseInvoiceSyncStatus,
  };
}

export default connect(
  mapStateToProps,
  {
    fetchPurchaseInvoices: _fetchPurchaseInvoices,
    triggerPurchaseInvoiceSync: _triggerPurchaseInvoiceSync,
  },
)(translate('ChainInvoicingView')(PurchaseInvoiceList));
